import React, { useState } from "react";
import { Link } from "react-router-dom";
import { getConsultationPath } from "../../../utils/consultationLink";
import { WEB_LEAD_MODULES } from "../../../constants/webLeadModules";
import {
  ArrowLeft,
  Calculator,
  RotateCcw,
  Target,
  IndianRupee,
  Clock,
  Percent,
  Heart,
  BookOpen,
  Car,
  Home,
  Sunset,
  Baby,
  Palmtree,
  TrendingUp,
  Flag,
  Info,
} from "lucide-react";
import Navbar from "../../../components/Navbar/Navbar";
import Footer from "../../../components/Footer/Footer";
import Swal from "sweetalert2";
import "./GoalPlanningCalculator.css";

const GOALS = [
  {
    value: "marriage",
    label: "Marriage",
    desc: "Plan your dream wedding",
    color: "#e11d48",
    colorEnd: "#fb7185",
    Icon: Heart,
  },
  {
    value: "education",
    label: "Education",
    desc: "Fund your child's education",
    color: "#2563eb",
    colorEnd: "#60a5fa",
    Icon: BookOpen,
  },
  {
    value: "car",
    label: "Car Purchase",
    desc: "Drive your dream car",
    color: "#0891b2",
    colorEnd: "#38bdf8",
    Icon: Car,
  },
  {
    value: "home",
    label: "Home Purchase",
    desc: "Buy your dream home",
    color: "#7c3aed",
    colorEnd: "#a78bfa",
    Icon: Home,
  },
  {
    value: "retirement",
    label: "Retirement",
    desc: "Retire comfortably",
    color: "#f59e0b",
    colorEnd: "#fcd34d",
    Icon: Sunset,
  },
  {
    value: "child",
    label: "Child's Future",
    desc: "Secure your child's tomorrow",
    color: "#10b981",
    colorEnd: "#6ee7b7",
    Icon: Baby,
  },
  {
    value: "vacation",
    label: "Dream Vacation",
    desc: "Travel the world in style",
    color: "#8b5cf6",
    colorEnd: "#c4b5fd",
    Icon: Palmtree,
  },
];

const formatINR = (n) =>
  Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const getMilestoneLabel = (pct) => {
  if (pct >= 100) return { label: "Goal Achieved! 🎉", color: "#10b981" };
  if (pct >= 75) return { label: "75% Milestone", color: "#0891b2" };
  if (pct >= 50) return { label: "Halfway There!", color: "#7c3aed" };
  if (pct >= 25) return { label: "25% Milestone", color: "#f59e0b" };
  return null;
};

export default function GoalPlanningCalculator() {
  const [goalType, setGoalType] = useState("marriage");
  const [futureValue, setFutureValue] = useState("");
  const [rate, setRate] = useState("");
  const [years, setYears] = useState("");
  const [result, setResult] = useState(null);

  const active = GOALS.find((g) => g.value === goalType);
  const accentColor = active?.color || "#2563eb";
  const accentEnd = active?.colorEnd || "#60a5fa";

  const handleCalculate = () => {
    if (!futureValue || !rate || !years) {
      Swal.fire({
        title: "Missing Details",
        text: "Please fill in all fields to calculate your goal SIP.",
        icon: "warning",
        confirmButtonText: "Got it",
        confirmButtonColor: accentColor,
        width: 380,
      });
      return;
    }

    const FV = Number(futureValue);
    const r = Number(rate) / 100 / 12;
    const n = Number(years) * 12;
    const den = ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
    const sip = FV / den;

    const totalInvestment = sip * n;
    const wealthGained = FV - totalInvestment;
    const investedPct = Math.round((totalInvestment / FV) * 100);

    // Year-by-year growth breakdown
    const breakdown = [];
    let corpus = 0;
    for (let y = 1; y <= Number(years); y++) {
      let yInvested = 0;
      for (let m = 0; m < 12; m++) {
        corpus = (corpus + sip) * (1 + r);
        yInvested += sip;
      }
      const progressPct = Math.min(100, Math.round((corpus / FV) * 100));
      const milestone = getMilestoneLabel(progressPct);
      breakdown.push({
        year: y,
        invested: Math.round(yInvested),
        cumInvested: Math.round(sip * y * 12),
        corpus: Math.round(corpus),
        gains: Math.round(corpus - sip * y * 12),
        progressPct,
        milestone,
      });
    }

    setResult({
      sip: Math.round(sip),
      totalInvest: Math.round(totalInvestment),
      wealth: Math.round(wealthGained),
      fv: FV,
      investedPct,
      gainedPct: 100 - investedPct,
      breakdown,
    });
  };

  const handleReset = () => {
    setFutureValue("");
    setRate("");
    setYears("");
    setResult(null);
  };

  return (
    <div className="gp-wrapper">
      <Navbar />

      {/* ── Hero ── */}
      <section className="gp-hero">
        <div className="gp-hero-bg" />
        <div className="gp-hero-dots" />
        <div className="gp-hero-glow gp-glow-l" />
        <div className="gp-hero-glow gp-glow-r" />

        <div className="gp-hero-inner">
          <Link to="/calculators" className="gp-back-btn">
            <ArrowLeft size={14} /> Back to Calculators
          </Link>

          <div className="gp-hero-top">
            <div>
              <div className="gp-eyebrow">
                <Target size={12} /> Goal Planning Calculators
              </div>
              <h1 className="gp-hero-title">
                Achieve Every Goal
                <br />
                <span
                  className="gp-gradient-text"
                  style={{
                    backgroundImage: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                  }}
                >
                  with a Smart SIP Plan
                </span>
              </h1>
              <p className="gp-hero-sub">
                Tell us your dream and timeline — we'll tell you exactly how
                much to invest every month to get there.
              </p>
            </div>

            <div
              className="gp-hero-icon-box"
              style={{
                background: `linear-gradient(135deg, ${accentColor}, ${accentEnd})`,
              }}
            >
              {active ? (
                <active.Icon size={36} color="#fff" />
              ) : (
                <Target size={36} color="#fff" />
              )}
            </div>
          </div>

          {/* Goal chips */}
          <div className="gp-goal-chips">
            {GOALS.map(({ value, label, color, Icon }) => (
              <button
                key={value}
                className={`gp-chip${goalType === value ? " gp-chip--active" : ""}`}
                style={
                  goalType === value
                    ? { borderColor: color, background: `${color}14`, color }
                    : {}
                }
                onClick={() => {
                  setGoalType(value);
                  setResult(null);
                }}
              >
                <Icon size={13} />
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Main ── */}
      <div className="gp-content">
        {/* Input Card */}
        <div className="gp-card">
          <div
            className="gp-card-bar"
            style={{
              background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
            }}
          />
          <div className="gp-card-body">
            {/* Active goal pill */}
            <div
              className="gp-active-pill"
              style={{
                background: `${accentColor}10`,
                borderColor: `${accentColor}22`,
                color: accentColor,
              }}
            >
              {active && <active.Icon size={13} />}
              {active?.label} — {active?.desc}
            </div>

            <div className="gp-section-title">
              Goal Details
              <span className="gp-section-line" />
            </div>

            <div className="gp-grid-2">
              {/* Future Goal Cost */}
              <div className="gp-field">
                <label className="gp-label">
                  <IndianRupee size={12} /> Future Goal Cost (₹)
                </label>
                <div className="gp-input-wrap">
                  <span className="gp-input-icon">
                    <IndianRupee size={14} />
                  </span>
                  <input
                    type="number"
                    className="gp-input"
                    placeholder="e.g., 1500000"
                    value={futureValue}
                    onChange={(e) => setFutureValue(e.target.value)}
                  />
                </div>
              </div>

              {/* Years */}
              <div className="gp-field">
                <label className="gp-label">
                  <Clock size={12} /> Years to Achieve Goal
                </label>
                <div className="gp-input-wrap">
                  <span className="gp-input-icon">
                    <Clock size={14} />
                  </span>
                  <input
                    type="number"
                    className="gp-input"
                    placeholder="e.g., 10"
                    value={years}
                    onChange={(e) => setYears(e.target.value)}
                  />
                </div>
              </div>

              {/* Rate */}
              <div className="gp-field">
                <label className="gp-label">
                  <Percent size={12} /> Expected Return (% p.a.)
                </label>
                <div className="gp-input-wrap">
                  <span className="gp-input-icon">
                    <Percent size={14} />
                  </span>
                  <input
                    type="number"
                    className="gp-input"
                    placeholder="e.g., 12"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="gp-btn-row">
              <button
                className="gp-calc-btn"
                style={{
                  background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                }}
                onClick={handleCalculate}
              >
                <Calculator size={16} /> Calculate SIP
              </button>
              <button className="gp-reset-btn" onClick={handleReset}>
                <RotateCcw size={15} /> Reset
              </button>
            </div>
          </div>
        </div>

        {/* ── Results ── */}
        {result && (
          <>
            {/* Summary + Bars */}
            <div className="gp-card">
              <div
                className="gp-card-bar"
                style={{
                  background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                }}
              />
              <div className="gp-card-body">
                <div className="gp-section-title">
                  Your Goal SIP Plan
                  <span className="gp-section-line" />
                </div>

                {/* Result cards */}
                <div className="gp-result-grid">
                  {[
                    {
                      label: "Monthly SIP Needed",
                      value: result.sip,
                      color: accentColor,
                      highlight: true,
                    },
                    {
                      label: "Total Investment",
                      value: result.totalInvest,
                      color: "#0891b2",
                    },
                    {
                      label: "Wealth Gained",
                      value: result.wealth,
                      color: "#10b981",
                    },
                    {
                      label: "Future Goal Value",
                      value: result.fv,
                      color: "#7c3aed",
                    },
                  ].map(({ label, value, color, highlight }) => (
                    <div
                      key={label}
                      className={`gp-result-card${highlight ? " gp-result-card--highlight" : ""}`}
                      style={{
                        background: highlight
                          ? `linear-gradient(135deg, ${color}14, ${color}22)`
                          : `linear-gradient(135deg, ${color}08, ${color}14)`,
                        borderColor: `${color}${highlight ? "44" : "28"}`,
                      }}
                    >
                      {highlight && (
                        <div
                          className="gp-result-badge"
                          style={{ background: `${color}18`, color }}
                        >
                          Recommended
                        </div>
                      )}
                      <div className="gp-result-label">{label}</div>
                      <div className="gp-result-value" style={{ color }}>
                        ₹ {formatINR(value)}
                      </div>
                      {highlight && (
                        <div className="gp-result-hint">
                          per month for {years} years
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Breakdown bars */}
                <div className="gp-section-title gp-section-title--spaced">
                  Investment Breakdown
                  <span className="gp-section-line" />
                </div>

                {[
                  {
                    label: "Amount You Invest",
                    pct: result.investedPct,
                    amt: result.totalInvest,
                    color: "#0891b2",
                  },
                  {
                    label: "Returns Generated",
                    pct: result.gainedPct,
                    amt: result.wealth,
                    color: accentColor,
                  },
                ].map(({ label, pct, amt, color }) => (
                  <div key={label} className="gp-bar-group">
                    <div className="gp-bar-row">
                      <span className="gp-bar-label">
                        {label} ({pct}%)
                      </span>
                      <span className="gp-bar-amt">₹ {formatINR(amt)}</span>
                    </div>
                    <div className="gp-bar-track">
                      <div
                        className="gp-bar-fill"
                        style={{
                          width: `${pct}%`,
                          background: `linear-gradient(90deg, ${color}, ${color}88)`,
                        }}
                      />
                    </div>
                  </div>
                ))}

                {/* Motivational footer */}
                <div
                  className="gp-motivate-banner"
                  style={{
                    background: `linear-gradient(135deg, ${accentColor}0a, ${accentEnd}14)`,
                    borderColor: `${accentColor}20`,
                  }}
                >
                  {active && (
                    <active.Icon
                      size={18}
                      style={{ color: accentColor, flexShrink: 0 }}
                    />
                  )}
                  <span>
                    Start a SIP of{" "}
                    <strong style={{ color: accentColor }}>
                      ₹ {formatINR(result.sip)}/month
                    </strong>{" "}
                    today and your <strong>{active?.label}</strong> goal of{" "}
                    <strong style={{ color: accentColor }}>
                      ₹ {formatINR(result.fv)}
                    </strong>{" "}
                    will be ready in <strong>{years} years</strong>. 🎯
                  </span>
                </div>
              </div>
            </div>

            {/* ── Yearly Growth Table ── */}
            <div className="gp-card">
              <div
                className="gp-card-bar"
                style={{
                  background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                }}
              />
              <div className="gp-card-body">
                <div className="gp-section-title">
                  <TrendingUp size={13} style={{ color: accentColor }} />
                  Yearly Corpus Growth Schedule
                  <span className="gp-section-line" />
                </div>

                {/* Info note */}
                <div className="gp-table-note">
                  <Info size={12} />
                  Showing how your corpus grows year-by-year toward your goal of
                  ₹ {formatINR(result.fv)}
                </div>

                <div className="gp-table-wrap">
                  <table className="gp-table">
                    <thead>
                      <tr>
                        {[
                          "Year",
                          "Invested This Year (₹)",
                          "Total Invested (₹)",
                          "Gains So Far (₹)",
                          "Corpus Value (₹)",
                          "Progress",
                        ].map((h) => (
                          <th key={h} className="gp-th">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.breakdown.map((row, i) => (
                        <tr
                          key={row.year}
                          className={i % 2 === 0 ? "gp-tr-even" : "gp-tr-odd"}
                        >
                          <td className="gp-td gp-td-year">Year {row.year}</td>
                          <td className="gp-td gp-td-mono">
                            {formatINR(row.invested)}
                          </td>
                          <td className="gp-td gp-td-mono">
                            {formatINR(row.cumInvested)}
                          </td>
                          <td className="gp-td gp-td-mono gp-td-gains">
                            {formatINR(row.gains)}
                          </td>
                          <td className="gp-td gp-td-mono gp-td-corpus">
                            {formatINR(row.corpus)}
                          </td>
                          <td className="gp-td">
                            <div className="gp-progress-cell">
                              <div className="gp-progress-bar-mini">
                                <div
                                  className="gp-progress-fill-mini"
                                  style={{
                                    width: `${row.progressPct}%`,
                                    background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                                  }}
                                />
                              </div>
                              <span className="gp-progress-pct">
                                {row.progressPct}%
                              </span>
                              {row.milestone && (
                                <span
                                  className="gp-milestone-flag"
                                  style={{
                                    color: row.milestone.color,
                                    background: `${row.milestone.color}12`,
                                    borderColor: `${row.milestone.color}30`,
                                  }}
                                >
                                  <Flag size={9} />
                                  {row.milestone.label}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {/* CTA Banner */}
        <div className="gp-cta-banner">
          <div className="gp-cta-text">
            <h3 className="gp-cta-title">
              Need help building your goal roadmap?
            </h3>
            <p className="gp-cta-sub">
              Our advisors will map every life goal to the right fund and SIP
              amount — for free.
            </p>
          </div>
          <Link to={getConsultationPath(WEB_LEAD_MODULES.CALC_GOAL)} className="gp-cta-btn">
            Book Free Consultation →
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
